import { ArrowUpRight, BarChart3, Brain, Boxes, Cpu, LayoutDashboard, Network, Database, ShieldCheck, Search } from "lucide-react";

const SOLS = [
  { name: "AI Over BI", icon: BarChart3 },
  { name: "Enterprise Brain", icon: Brain },
  { name: "Data Products", icon: Boxes },
  { name: "AI Models", icon: Cpu },
  { name: "Dashboards & Reporting", icon: LayoutDashboard },
];
const CAPS = [
  { icon: Network, title: "Context Generation", desc: "Automatically discover and map the relationships and meaning across enterprise data, processes and knowledge." },
  { icon: Database, title: "Context Persistence", desc: "Store adaptive context across knowledge graphs, vector stores and enterprise memory over time." },
  { icon: ShieldCheck, title: "Context Validation", desc: "Continuously check context for completeness, consistency, conflicts and freshness." },
  { icon: Search, title: "Context Retrieval", desc: "Rank and assemble the most relevant context for every query, agent and task." },
];

const RailItem = ({ x, on }) => (
  <div className={`text-left rounded-lg px-4 py-3 flex items-center gap-3 ${on ? "bg-white/70" : ""}`}>
    <span className={`w-8 h-8 rounded-lg flex items-center justify-center shrink-0 ${on ? "bg-[#8A5730] text-white" : "bg-[#3E2818]/5 text-[#3E2818]/45"}`}><x.icon size={15} strokeWidth={1.9} /></span>
    <span className={`text-[13.5px] font-semibold ${on ? "text-[#8A5730]" : "text-[#3E2818]/55"}`}>{x.name}</span>
  </div>
);

const Rail = () => SOLS.map((x, i) => <RailItem key={x.name} x={x} on={i === 1} />);

const Thumb = () => (
  <div className="rounded-xl overflow-hidden border border-[#3E2818]/12 shadow-[0_18px_48px_-30px_rgba(62,40,24,0.5)]">
    <div className="flex items-center gap-2 px-3.5 py-2.5 bg-[#F1EDE6] border-b border-[#3E2818]/8">
      <span className="w-2.5 h-2.5 rounded-full bg-[#E88B6B]" /><span className="w-2.5 h-2.5 rounded-full bg-[#E8C15E]" /><span className="w-2.5 h-2.5 rounded-full bg-[#9CB88A]" />
      <span className="ml-2 flex-1 truncate rounded-md bg-white/70 px-2.5 py-1 font-mono2 text-[10px] text-[#3E2818]/55">graph.demo.intugle.ai</span>
    </div>
    <div className="relative aspect-[16/10] overflow-hidden bg-white">
      <img src="/assets/enterprise-brain-demo.png" alt="demo" className="absolute inset-0 w-full h-full object-cover object-top" />
      <div className="absolute inset-x-0 bottom-0 h-2/5 bg-gradient-to-t from-[#F7F5F0] via-[#F7F5F0]/70 to-transparent" />
      <div className="absolute inset-x-0 bottom-0 p-4 flex items-end justify-between">
        <div><p className="font-mono2 text-[10px] uppercase tracking-[0.22em] text-[#8A5730]">Knowledge Graph Explorer</p><p className="text-[15px] font-semibold text-[#3E2818] mt-0.5">Explore the live demo</p></div>
        <span className="w-9 h-9 rounded-full bg-[#211307] text-white flex items-center justify-center"><ArrowUpRight size={18} /></span>
      </div>
    </div>
  </div>
);

const Hero = () => (
  <div className="grid md:grid-cols-[1fr_0.85fr] gap-8 items-center">
    <div>
      <h3 className="font-display text-2xl lg:text-3xl font-medium text-[#3E2818] tracking-tight leading-tight">Give AI a shared understanding of your business.</h3>
      <p className="text-sm md:text-base text-[#3E2818]/65 mt-3 leading-relaxed">Generate a semantic and contextual knowledge layer connecting enterprise data, metrics, entities, relationships, documents and institutional knowledge.</p>
    </div>
    <Thumb />
  </div>
);

const Caps = ({ cols = "sm:grid-cols-2" }) => (
  <div className={`grid ${cols} gap-3`}>
    {CAPS.map((c) => (
      <div key={c.title} className="rounded-xl bg-white/45 backdrop-blur-sm border border-white/60 p-4">
        <div className="w-9 h-9 rounded-lg bg-[#8A5730]/10 text-[#8A5730] flex items-center justify-center mb-3"><c.icon size={16} strokeWidth={1.9} /></div>
        <p className="text-[13px] font-semibold text-[#3E2818] leading-snug">{c.title}</p>
        <p className="text-[12px] text-[#3E2818]/60 mt-1.5 leading-relaxed">{c.desc}</p>
      </div>
    ))}
  </div>
);

const Frame = ({ children }) => <div className="rounded-2xl bg-[#EFE7D9] p-5 lg:p-7 border border-[#3E2818]/8">{children}</div>;

/* A · Full-height rail panel with footer CTA; caps 2x2 in detail */
const A = () => (
  <Frame>
    <div className="grid lg:grid-cols-[0.28fr_1fr] gap-6 lg:gap-10 items-stretch">
      <div className="rounded-xl bg-white/40 border border-white/60 p-2 flex flex-col">
        <div className="flex flex-col gap-1">{Rail()}</div>
        <div className="mt-auto p-3">
          <a className="flex items-center justify-between rounded-lg bg-[#211307] text-[#F7F5F0] px-4 py-3 text-[12.5px] font-semibold">Talk to our team <ArrowUpRight size={15} /></a>
        </div>
      </div>
      <div className="space-y-8">
        <Hero />
        <Caps />
      </div>
    </div>
  </Frame>
);

/* B · Vertically centered rail; caps 2x2 in detail */
const B = () => (
  <Frame>
    <div className="grid lg:grid-cols-[0.28fr_1fr] gap-6 lg:gap-12 items-stretch">
      <div className="flex flex-col justify-center gap-1 lg:border-r lg:border-[#3E2818]/12 lg:pr-4">{Rail()}</div>
      <div className="space-y-8">
        <Hero />
        <Caps />
      </div>
    </div>
  </Frame>
);

/* C · Rail top + descriptive block below in left column */
const C = () => (
  <Frame>
    <div className="grid lg:grid-cols-[0.28fr_1fr] gap-6 lg:gap-12 items-start">
      <div className="lg:border-r lg:border-[#3E2818]/12 lg:pr-4">
        <div className="flex flex-col gap-1">{Rail()}</div>
        <div className="mt-6 px-4">
          <p className="font-mono2 text-[10px] uppercase tracking-[0.22em] text-[#8A5730] mb-2">Why it matters</p>
          <p className="text-[12.5px] text-[#3E2818]/60 leading-relaxed">Every Intugle solution runs on the same trusted context layer — so intelligence compounds across your enterprise.</p>
        </div>
      </div>
      <div className="space-y-8">
        <Hero />
        <Caps />
      </div>
    </div>
  </Frame>
);

/* D · Rail full-height bordered card + big decorative icon at bottom; caps 2x2 */
const D = () => (
  <Frame>
    <div className="grid lg:grid-cols-[0.28fr_1fr] gap-6 lg:gap-10 items-stretch">
      <div className="rounded-xl border border-[#3E2818]/12 bg-white/30 p-2 flex flex-col overflow-hidden">
        <div className="flex flex-col gap-1">{Rail()}</div>
        <div className="mt-auto relative h-24">
          <Brain className="absolute -bottom-4 -right-2 text-[#8A5730]/12" size={120} strokeWidth={1} />
        </div>
      </div>
      <div className="space-y-8">
        <Hero />
        <Caps />
      </div>
    </div>
  </Frame>
);

const OPTIONS = [
  { id: "a", title: "Option A · Full-Height Rail Panel + CTA", note: "The left rail lives in a soft panel that spans the full height; a small dark CTA sits at the bottom to fill the space intentionally. Capabilities stay in the detail column (2×2).", comp: A },
  { id: "b", title: "Option B · Vertically Centered Rail", note: "The rail is vertically centered against the detail height, so the empty space is balanced above and below. Clean and simple. Capabilities stay 2×2 in the detail.", comp: B },
  { id: "c", title: "Option C · Rail + Supporting Note", note: "Rail on top, with a short supporting note beneath it in the left column. Uses the space to reinforce the message. Capabilities 2×2 in the detail.", comp: C },
  { id: "d", title: "Option D · Rail Card + Decorative Glyph", note: "Rail in a bordered card with a large, faint solution glyph anchoring the bottom. Distinctive and premium. Capabilities 2×2 in the detail.", comp: D },
];

export default function RailBalanceOptions() {
  return (
    <div className="min-h-screen bg-white">
      <div className="max-w-6xl mx-auto px-6 py-12">
        <p className="font-mono2 text-[10px] uppercase tracking-[0.3em] text-[#8A5730]">Section 5 · Rail + Capability Balance</p>
        <h1 className="font-display text-2xl lg:text-3xl font-medium mt-2 tracking-tight text-[#3E2818]">Keep capabilities in the detail column — balance the rail space</h1>
        <p className="text-sm text-[#3E2818]/60 mt-3 max-w-2xl leading-relaxed">Capabilities never sit under the rail; each option handles the left-column space differently. Tell me the letter.</p>
        <div className="mt-10 space-y-14">
          {OPTIONS.map((o) => {
            const C = o.comp;
            return (
              <div key={o.id}>
                <p className="font-mono2 text-[11px] uppercase tracking-[0.2em] text-[#B87A32] mb-4">{o.title}</p>
                <C />
                <p className="text-[12px] text-[#3E2818]/55 mt-3 leading-snug max-w-3xl">{o.note}</p>
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
}
